import { Form } from "@/components/ui/form";
import { DynamicFields } from "@/features/FormCard/DynamicCard";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  InputField,
  SelectFieldWithSection,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { NewDiff } from "@/features/FormCard/NewDiff";
import { useCurrencyOptions } from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import { useTranslation } from "react-i18next";
import { dynamicNewFormConfigWithoutId } from "./edit-form-conf";
import { getFieldTranslations } from "./translations";

const client = "a6";
const table_name = "a6_static_data";

export function A6StaticDataNew() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    selectFieldsConfig,
    isLoadingTranslations,
    optionsMap,
    options,
  } = useDynamicForm({
    client,
    table_name,
    formConfig: dynamicNewFormConfigWithoutId,
    isNew: true,
  });

  const assetClass1 = form.watch("asset_class_1");
  const assetClass2 = form.watch("asset_class_2");

  const currencyOptions = useCurrencyOptions();
  const fieldTranslations = getFieldTranslations(t);
  const translatedDynamicConfig = useMemo(() => {
    const cfg = selectFieldsConfig?.asset_class_2;
    if (!cfg) return undefined;

    const translatedSections = Object.fromEntries(
      Object.entries(cfg.section).map(([sectionName, fields]) => [
        sectionName,
        Object.fromEntries(
          Object.entries(fields).map(([fieldName, fieldConfig]) => [
            fieldName,
            {
              ...fieldConfig,
              label: t(`a6_static_data.columns.${fieldName}`, {
                ns: "tables",
                defaultValue: fieldConfig.label,
              }),
            },
          ]),
        ),
      ]),
    );

    return {
      ...cfg,
      section: translatedSections,
    };
  }, [selectFieldsConfig?.asset_class_2, t]);

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("a6_static_data.title", {
                  ns: "tables",
                  defaultValue: "Actif",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t("a6_static_data.sections.general_information", {
                    ns: "tables",
                    defaultValue: "Informations générales",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8">
                    <InputField
                      name="name"
                      label={t("a6_static_data.columns.name", {
                        ns: "tables",
                        defaultValue: "Nom",
                      })}
                      isRequired={true}
                      capitalize={true}
                      control={form.control}
                    />
                  </div>
                  <div className="md:col-span-4">
                    <AutocompleteLabelField
                      name="currency"
                      label={t("a6_static_data.columns.currency", {
                        ns: "tables",
                        defaultValue: "Devise",
                      })}
                      options={currencyOptions}
                      isRequired={true}
                      control={form.control}
                      className="min-w-0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                  <InputField
                    name="isin"
                    label={t("a6_static_data.columns.isin", {
                      ns: "tables",
                      defaultValue: "ISIN",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="code"
                    label={t("a6_static_data.columns.code", {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="ticker"
                    label={t("a6_static_data.columns.ticker", {
                      ns: "tables",
                      defaultValue: "Ticker",
                    })}
                    control={form.control}
                  />
                </div>
                <AutocompleteAsyncField
                  name="a6_issuer_name"
                  label={t("a6_static_data.columns.a6_issuer_name", {
                    ns: "tables",
                    defaultValue: "Émetteur",
                  })}
                  control={form.control}
                  base_url="a6/a6_issuers"
                  id_column="issuer_id"
                  column_name="name"
                />
                <SelectFieldWithSection
                  listboxWrapperClassNames="!h-[450px] !max-h-none"
                  primary_key="asset_class_1"
                  name="asset_class_2"
                  label={t("a6_static_data.columns.asset_class_2", {
                    ns: "tables",
                    defaultValue: "Classe d'actifs",
                  })}
                  control={form.control}
                  sections={optionsMap.asset_class_2}
                  placeholder={t("form.placeholders.select")}
                  isRequired
                />
                <Divider />
                <Label>
                  {t("sections.technical_info", {
                    defaultValue: "Informations techniques",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="strategy_id"
                    label={t("a6_static_data.columns.strategy_id", {
                      ns: "tables",
                      defaultValue: "ID de la stratégie",
                    })}
                    control={form.control}
                    type="number"
                  />
                </div>
                <DynamicFields
                  primaryValue={assetClass1}
                  fieldValue={assetClass2}
                  control={form.control}
                  config={translatedDynamicConfig}
                  isLoadingTranslations={isLoadingTranslations}
                  options={options}
                />
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  ["issuer_id"],
                  fieldTranslations,
                  table_name,
                )
              : t("no_changes")
          }
        />

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
